import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery } from '@apollo/react-hooks'
import { toastr } from 'react-redux-toastr'
import { Download, LoaderCircle, Link2, MapPin, Save, Share2, Siren, Trash2 } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Separator } from '@/components/ui/separator'
import { Textarea } from '@/components/ui/textarea'
import { formatDateTimeLocal } from '@/utils/timezone'
import * as Query from '../../../api/gql/query'
import { PHOTO_FOLDERS, UPDATE_PHOTO } from '../api'
import { describePhotosError, photosErrorKey } from '../lib/errors'
import { folderLabel, folderLabels } from '../lib/folders'
import { editableFields, PhotoUi } from '../lib/permissions'
import type { Photo, PhotoFolder, PhotoVisibility } from '../types'
import { orderedFolders } from './BatchMetadataFields'
import PhotoActivityList from './PhotoActivityList'
import PhotoReferences from './PhotoReferences'
import TagPicker from './TagPicker'

const NONE = '__none__'

interface Props {
  projectId: string
  photo: Photo
  ui: PhotoUi
  onCreateDefect?: (photoIds: string[]) => void
  onAttachToSnag?: (photoIds: string[]) => void
  onShare?: (photoIds: string[]) => void
  onDelete: () => void
}

interface Form {
  title: string
  description: string
  buildingId: string | null
  floorId: string | null
  trade: string | null
  folderId: string
  visibility: PhotoVisibility
  tagIds: string[]
}

const formOf = (p: Photo): Form => ({
  title: p.title ?? '',
  description: p.description ?? '',
  buildingId: p.building?.id ?? null,
  floorId: p.floor?.id ?? null,
  trade: p.trade,
  folderId: p.folder.id,
  visibility: p.visibility,
  tagIds: p.tags.map((x) => x.id),
})

const sameSet = (a: string[], b: string[]) => a.length === b.length && a.every((x) => b.indexOf(x) >= 0)

/** Only the fields that differ from the photo; empty text and cleared selects become null. */
export const changedInput = (photo: Photo, form: Form): Record<string, unknown> => {
  const before = formOf(photo)
  const input: Record<string, unknown> = {}
  if (form.title !== before.title) input.title = form.title.trim() || null
  if (form.description !== before.description) input.description = form.description.trim() || null
  if (form.buildingId !== before.buildingId) input.buildingId = form.buildingId
  if (form.floorId !== before.floorId) input.floorId = form.floorId
  if (form.trade !== before.trade) input.trade = form.trade
  if (form.folderId !== before.folderId) input.folderId = form.folderId
  if (form.visibility !== before.visibility) input.visibility = form.visibility
  if (!sameSet(form.tagIds, before.tagIds)) input.tagIds = form.tagIds
  return input
}

interface BuildingItem { id: string; name: string; code: string | null; floors: { id: string; name: string }[] }

const Row: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="flex items-start justify-between gap-3 text-sm">
    <span className="shrink-0 text-muted-foreground">{label}</span>
    <span className="min-w-0 text-right break-words">{children}</span>
  </div>
)

const PhotoDetailsPanel: React.FC<Props> = ({ projectId, photo, ui, onCreateDefect, onAttachToSnag, onShare, onDelete }) => {
  const { t, i18n } = useTranslation()
  const [form, setForm] = useState<Form>(() => formOf(photo))
  const fields = editableFields(photo, ui)
  const { data: folderData } = useQuery<{ photoFolders: PhotoFolder[] }>(PHOTO_FOLDERS, { variables: { projectId }, skip: !projectId })
  const { data: buildingData } = useQuery(Query.BUILDINGS, { variables: { projectId }, skip: !projectId })
  const { data: settingsData } = useQuery(Query.SNAG_SETTINGS, { variables: { projectId }, skip: !projectId })
  const [updatePhoto, { loading: saving }] = useMutation(UPDATE_PHOTO, {
    context: { handlesErrors: true },
    refetchQueries: ['photoActivity', 'photoFolders'],
  })

  const folders = useMemo(() => orderedFolders(folderData?.photoFolders ?? []), [folderData])
  const buildings: BuildingItem[] = buildingData?.buildings ?? []
  const trades: string[] = settingsData?.snagSettings?.infrastructureTypes ?? []
  const floors = buildings.find((b) => b.id === form.buildingId)?.floors ?? []
  const input = changedInput(photo, form)
  const dirty = Object.keys(input).length > 0
  const set = (patch: Partial<Form>) => setForm({ ...form, ...patch })

  const save = async () => {
    try {
      const res = await updatePhoto({ variables: { id: photo.id, input } })
      setForm(formOf(res.data.updatePhoto))
    } catch (e: any) {
      toastr.error(t('Common_error'), describePhotosError(photosErrorKey(e), t))
    }
  }

  const pick = (id: string, value: string | null, onPick: (v: string | null) => void, items: { id: string; label: string }[], disabled: boolean) => (
    <Select value={value || NONE} onValueChange={(v) => onPick(v === NONE ? null : v)} disabled={disabled}>
      <SelectTrigger id={id} className="h-9"><SelectValue /></SelectTrigger>
      <SelectContent>
        <SelectItem value={NONE}>{t('Common_none')}</SelectItem>
        {items.map((i) => <SelectItem key={i.id} value={i.id}>{i.label}</SelectItem>)}
      </SelectContent>
    </Select>
  )

  const field = (id: string, label: string, control: React.ReactNode) => (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      {control}
    </div>
  )

  const hasGps = photo.latitude != null && photo.longitude != null

  return (
    <div className="space-y-4 p-4">
      <div className="space-y-2">
        <Row label={t('Photos_meta_captured')}>
          {formatDateTimeLocal(photo.capturedAt, i18n.language)}{' '}
          <Badge variant={photo.timeSource === 'EXIF' ? 'success' : 'neutral'} className="whitespace-nowrap">
            {photo.timeSource === 'EXIF' ? t('Photos_time_source_exif') : t('Photos_no_exif_time')}
          </Badge>
        </Row>
        <Row label={t('Photos_meta_uploaded')}>{formatDateTimeLocal(photo.createdAt, i18n.language)}</Row>
        <Row label={t('Photos_filter_uploader')}>{photo.uploadedBy.name}</Row>
        {photo.device && <Row label={t('Photos_meta_device')}>{photo.device}</Row>}
        {hasGps && (
          <Row label={t('Photos_meta_gps')}>
            <a
              href={`https://www.google.com/maps?q=${photo.latitude},${photo.longitude}`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-primary hover:underline"
            >
              <MapPin className="h-3.5 w-3.5" />
              {photo.latitude!.toFixed(5)}, {photo.longitude!.toFixed(5)}
            </a>
          </Row>
        )}
        {photo.heading != null && <Row label={t('Photos_meta_heading')}>{`${Math.round(photo.heading)}°`}</Row>}
      </div>

      <Separator />

      <div className="space-y-4">
        {field('photo-title', t('Photos_field_title'), (
          <Input id="photo-title" className="h-9" value={form.title} disabled={!fields.own} onChange={(e) => set({ title: e.target.value })} />
        ))}
        {field('photo-description', t('Photos_field_description'), (
          <Textarea id="photo-description" rows={3} value={form.description} disabled={!fields.own} onChange={(e) => set({ description: e.target.value })} />
        ))}
        {buildings.length > 0 && (
          <div className="grid grid-cols-2 gap-3">
            {field('photo-building', t('Photos_field_building'), pick(
              'photo-building',
              form.buildingId,
              (v) => set({ buildingId: v, floorId: null }),
              buildings.map((b) => ({ id: b.id, label: b.code ? `${b.code} - ${b.name}` : b.name })),
              !fields.organise,
            ))}
            {field('photo-floor', t('Photos_field_floor'), pick('photo-floor', form.floorId, (v) => set({ floorId: v }), floors.map((f) => ({ id: f.id, label: f.name })), !fields.organise))}
          </div>
        )}
        {trades.length > 0 && field('photo-trade', t('Photos_filter_trade'), pick('photo-trade', form.trade, (v) => set({ trade: v }), trades.map((tr) => ({ id: tr, label: tr })), !fields.organise))}
        {field('photo-tags', t('Photos_filter_tags'), (
          fields.organise
            ? <TagPicker projectId={projectId} value={form.tagIds} onChange={(tagIds) => set({ tagIds })} allowCreate={ui.canManageStructure} />
            : <div className="flex flex-wrap gap-1.5">{photo.tags.map((tag) => <Badge key={tag.id} variant="secondary" className="whitespace-nowrap">{tag.name}</Badge>)}</div>
        ))}
        {field('photo-folder', t('Photos_field_folder'), (
          <Select value={form.folderId} onValueChange={(v) => set({ folderId: v })} disabled={!fields.organise}>
            <SelectTrigger id="photo-folder" className="h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              {folders.map(({ folder, depth }) => (
                <SelectItem key={folder.id} value={folder.id}>{`${'– '.repeat(depth)}${folderLabel(folder, folderLabels(t))}`}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        ))}
        {field('photo-visibility', t('Photos_filter_visibility'), (
          <Select value={form.visibility} onValueChange={(v) => set({ visibility: v as PhotoVisibility })} disabled={!fields.own}>
            <SelectTrigger id="photo-visibility" className="h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="PROJECT">{t('Photos_visibility_project')}</SelectItem>
              <SelectItem value="INTERNAL">{t('Photos_visibility_internal')}</SelectItem>
            </SelectContent>
          </Select>
        ))}
        {(fields.own || fields.organise) && (
          <Button className="w-full" disabled={!dirty || saving} onClick={save}>
            {saving ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            {t('Common_save')}
          </Button>
        )}
      </div>

      <Separator />

      <div className="space-y-2">
        <h3 className="text-sm font-semibold">{t('Photos_albums')}</h3>
        {photo.albums.length === 0
          ? <p className="text-sm text-muted-foreground">{t('Photos_albums_none')}</p>
          : <div className="flex flex-wrap gap-1.5">{photo.albums.map((a) => <Badge key={a.id} variant="secondary" className="whitespace-nowrap">{a.name}</Badge>)}</div>}
      </div>

      <Separator />

      <PhotoReferences projectId={projectId} photoId={photo.id} />

      <Separator />

      <div className="flex flex-wrap gap-2">
        {photo.originalUrl && (
          <Button variant="outline" size="sm" asChild>
            <a href={photo.originalUrl} download={photo.filename ?? undefined} target="_blank" rel="noreferrer">
              <Download className="h-4 w-4" />
              {t('Photos_download_original')}
            </a>
          </Button>
        )}
        {onCreateDefect && (
          <Button variant="outline" size="sm" onClick={() => onCreateDefect([photo.id])}>
            <Siren className="h-4 w-4" />
            {t('Photos_create_defect')}
          </Button>
        )}
        {onAttachToSnag && (
          <Button variant="outline" size="sm" onClick={() => onAttachToSnag([photo.id])}>
            <Link2 className="h-4 w-4" />
            {t('Photos_attach_to_snag')}
          </Button>
        )}
        {onShare && (
          <Button variant="outline" size="sm" onClick={() => onShare([photo.id])}>
            <Share2 className="h-4 w-4" />
            {t('Photos_share')}
          </Button>
        )}
        {photo.canDelete && (
          <Button variant="outline" size="sm" className="text-destructive hover:text-destructive" onClick={onDelete}>
            <Trash2 className="h-4 w-4" />
            {t('Common_delete')}
          </Button>
        )}
      </div>

      <Separator />

      <div className="space-y-2">
        <h3 className="text-sm font-semibold">{t('Photos_activity')}</h3>
        <PhotoActivityList photoId={photo.id} />
      </div>
    </div>
  )
}

export default PhotoDetailsPanel
